/**
 * VERIX ERP — RBAC permission codes.
 *
 * These values must match exactly the `code` column in the `permissions` DB table.
 * Always use these constants — never use string literals — to prevent typos and
 * make refactors safe.
 *
 * Convention: MODULE_ACTION  →  'module.action'
 */

export const PERMS = {
  // Accounting
  ACCOUNTING_VIEW:           'accounting.view',
  ACCOUNTING_EDIT:           'accounting.edit',
  ACCOUNTING_EXPORT:         'accounting.export',
  ACCOUNTING_IMPORT:         'accounting.import',
  // Least-privilege para el plan de cuentas (PGC); el asesor externo los recibe
  // sin abrir todo accounting.edit. Ver verix-api rbacSeed.ts.
  ACCOUNTING_CREATE_ACCOUNT: 'accounting.create_account',
  ACCOUNTING_ONBOARD:        'accounting.onboard',

  // Invoicing
  INVOICING_VIEW:        'invoicing.view',
  INVOICING_CREATE:      'invoicing.create',
  INVOICING_EDIT:        'invoicing.edit',
  INVOICING_DELETE:      'invoicing.delete',
  INVOICING_SEND:        'invoicing.send',

  // Expenses
  EXPENSES_VIEW:         'expenses.view',
  EXPENSES_CREATE:       'expenses.create',
  EXPENSES_APPROVE:      'expenses.approve',
  EXPENSES_EXPORT:       'expenses.export',

  // Banking
  BANKING_VIEW:          'banking.view',
  BANKING_RECONCILE:     'banking.reconcile',

  // HR
  HR_VIEW:               'hr.view',
  HR_EDIT:               'hr.edit',
  HR_EXPORT:             'hr.export',

  // Employees
  EMPLOYEES_VIEW:        'employees.view',
  EMPLOYEES_EDIT:        'employees.edit',
  EMPLOYEES_DELETE:      'employees.delete',
  EMPLOYEES_SENSITIVE:   'employees.sensitive',

  // Payroll
  PAYROLL_VIEW:          'payroll.view',
  PAYROLL_EDIT:          'payroll.edit',
  PAYROLL_CONFIRM:       'payroll.confirm',
  PAYROLL_EXPORT:        'payroll.export',

  // Absences
  ABSENCES_VIEW:         'absences.view',
  ABSENCES_APPROVE:      'absences.approve',

  // Approvals
  APPROVALS_VIEW:        'approvals.view',
  APPROVALS_MANAGE:      'approvals.manage',
  APPROVALS_RULES:       'approvals.rules',

  // CRM
  CRM_VIEW:              'crm.view',
  CRM_EDIT:              'crm.edit',
  CRM_DELETE:            'crm.delete',

  // Projects
  PROJECTS_VIEW:         'projects.view',
  PROJECTS_EDIT:         'projects.edit',
  PROJECTS_DELETE:       'projects.delete',

  // Inventory
  INVENTORY_VIEW:        'inventory.view',
  INVENTORY_EDIT:        'inventory.edit',

  // Analytics
  ANALYTICS_VIEW:        'analytics.view',
  ANALYTICS_EXPORT:      'analytics.export',

  // Settings
  SETTINGS_VIEW:         'settings.view',
  SETTINGS_EDIT:         'settings.edit',

  // Roles
  ROLES_MANAGE:          'roles.manage',
  ROLES_VIEW:            'roles.view',

  // Automation
  AUTOMATION_VIEW:       'automation.view',
  AUTOMATION_EDIT:       'automation.edit',

  // Fiscal
  //
  // Tres escalones, y la distinción del medio importa: PREPARAR no es DECLARAR.
  // Quien prepara no compromete a nadie ante la AEAT; quien presenta, sí. El
  // escalón intermedio nace con el paso 3 del Modelo 180 —rellenar un 180 dura
  // días, hay que pedirle la referencia catastral a cada arrendador— y sin él
  // capturar un dato exigiría poder presentarlo, que es dar de más.
  FISCAL_VIEW:           'fiscal.view',
  FISCAL_EDIT:           'fiscal.edit',
  FISCAL_SUBMIT:         'fiscal.submit',

  // Compliance (Fase 8 / Bloque R) — sembrados en verix-api rbacSeed + seed-roles.
  COMPLIANCE_AUDIT_READ:     'compliance.audit_logs.read',
  COMPLIANCE_RETENTION_READ: 'compliance.retention.read',
  COMPLIANCE_BACKUP_EXPORT:  'compliance.backup.export',

  // Asesor (Portal del Asesor — FASE 5)
  // Acceso a la lista cross-empresa que un accountant_external gestiona.
  // El backend lo añade al rol "Accountant External" en empresas nuevas
  // (rbacSeed.ts ACCOUNTANT_EXTERNAL_PERMS). Companies legacy reciben este
  // permiso vía scripts/backfill-asesor-view-permission.ts.
  ASESOR_VIEW:           'asesor.view',

  // OCR (document scanning + extraction)
  OCR_SCAN:              'ocr.scan',
  OCR_REVIEW:            'ocr.review',
  OCR_DELETE:            'ocr.delete',

  // Archivo documental. Separado de OCR_REVIEW porque son cosas distintas:
  // revisar la cola de escaneos vs consultar lo ya archivado. Nació dentro de la
  // Bandeja y heredó su permiso; esto lo desacopla.
  DOCUMENTS_VIEW:        'documents.view',

  // HR ID scan (DNI/NIE) — separate from OCR_SCAN because it's GDPR-sensitive
  // Base legal: Art. 6.1.b (contractual) — necesario para ejecutar el contrato laboral.
  HR_ID_SCAN:            'hr.id_scan',

  // AI assistant (Aria) — gates the /asistente-agentic page and the floating chat.
  // Seeded for Admin / HR Manager / Accountant / Supervisor by default; Employee
  // (read-only) is excluded.
  AI_USE:                'ai.use',
} as const;

/**
 * Roles que leen facturas sin tener `invoicing.view`: los de `INVOICE_WRITE_ROLES` en
 * verix-api (routes/invoicing/invoices.ts) menos admin y owner, que ya pasan por el bypass
 * de `usePermissions`. `manager` se siembra sin permisos: sin esta lista, el ERP le
 * escondía lo que la API sí le sirve.
 */
export const INVOICE_READ_ROLES: readonly string[] = ['manager', 'accountant'];

export type PermissionCode = typeof PERMS[keyof typeof PERMS];
